/**
 * Weekly Commission Report — replaces the Master Commission Worksheet.
 *
 * Designed for Jamie's weekly Monday review: company pulse at the top,
 * recruiter distribution in the middle, per-contractor detail at the bottom.
 * Every number has context — trend, delta, or color.
 *
 * Shows ALL data the spreadsheet showed: pay rate, bill rate, load factor,
 * sub-vendor cost, GP/hr, hours, GP, share, allocated GP — plus enhancements
 * the spreadsheet never had (sparklines, deltas, tier badges, conditional color).
 */

import { useState } from 'react';

import { api, type WeeklyCommissionData, type WeeklyRecruiter } from './api';
import { MiniSparkline } from './components/MiniSparkline';
import { UnifiedTimeFilter } from './components/UnifiedTimeFilter';
import { useTimeFilter } from './context/TimeFilterContext';
import { Card, CardHeader, ExportButton, Spinner } from './components/Ui';
import { KpiCard, computeTrend } from './components/KpiCard';
import { useAsyncData } from './hooks/useAsyncData';
import { hours, money, moneyCompact, rate, shortDate } from './format';
import { Shell, type ShellProps } from './Shell';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Main component                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

export function WeeklyCommissionReport(props: ShellProps) {
  const tf = useTimeFilter();
  const [expandedRecruiter, setExpandedRecruiter] = useState<string | null>(null);

  const { data, error, loading } = useAsyncData<WeeklyCommissionData>(
    () => api.weeklyCommission(tf.toWeekEnding()),
    [tf.selection],
    'Could not load commission report.',
  );

  return (
    <Shell {...props}>
      {/* ── Header ── */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">Weekly Commission Report</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Company-wide GP and commission by recruiter
            {data?.weekEnding && (
              <>
                {' '}
                · Week ending{' '}
                <span className="font-medium text-ink-muted">{shortDate(data.weekEnding)}</span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data && data.recruiters.length > 0 && (
            <ExportButton
              data={data.recruiters.flatMap((r) =>
                r.contractors.map((c) => ({
                  recruiter: r.name,
                  role: r.role,
                  contractor: c.name,
                  client: c.clientName,
                  hours: c.hours,
                  payRate: c.payRate ?? '',
                  billRate: c.billRate ?? '',
                  loadFactor: c.loadFactor ?? '',
                  subVendor: c.subVendorRate ?? '',
                  gpPerHour: c.gpPerHour ?? '',
                  grossProfit: c.grossProfit,
                  share: `${(Number(c.share) * 100).toFixed(0)}%`,
                  allocatedGp: c.allocatedGp,
                })),
              )}
              filename={`commission-report-${data.weekEnding ?? 'latest'}`}
              headers={[
                { key: 'recruiter', label: 'Recruiter' },
                { key: 'role', label: 'Role' },
                { key: 'contractor', label: 'Contractor' },
                { key: 'client', label: 'Client' },
                { key: 'hours', label: 'Hours' },
                { key: 'payRate', label: 'Pay Rate' },
                { key: 'billRate', label: 'Bill Rate' },
                { key: 'loadFactor', label: 'Load Factor' },
                { key: 'subVendor', label: 'Sub-Vendor' },
                { key: 'gpPerHour', label: 'GP/Hr' },
                { key: 'grossProfit', label: 'Gross Profit' },
                { key: 'share', label: 'Share' },
                { key: 'allocatedGp', label: 'Allocated GP' },
              ]}
            />
          )}
          <UnifiedTimeFilter />
        </div>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {loading && (
        <div className="flex justify-center py-12">
          <Spinner label="Loading commission data…" />
        </div>
      )}

      {data && data.weekEnding && (
        <>
          {/* ── KPI Strip ── */}
          <KpiStrip data={data} />

          {/* ── GP Distribution Donut + Legend ── */}
          {data.recruiters.length > 0 && (
            <GpDistributionDonut recruiters={data.recruiters} companyGp={data.companyTotal.gp} />
          )}

          {/* ── Per-recruiter cards, separated by role ── */}
          <RecruiterSection
            title="Recruiters"
            recruiters={data.recruiters.filter((r) => r.role === 'recruiter')}
            allRecruiters={data.recruiters}
            expanded={expandedRecruiter}
            onToggle={(id) => setExpandedRecruiter(expandedRecruiter === id ? null : id)}
            tiers={data.tiers}
          />
          <RecruiterSection
            title="Salespeople"
            recruiters={data.recruiters.filter((r) => r.role === 'salesperson')}
            allRecruiters={data.recruiters}
            expanded={expandedRecruiter}
            onToggle={(id) => setExpandedRecruiter(expandedRecruiter === id ? null : id)}
            tiers={data.tiers}
          />
          {/* If no role distinction yet, show everyone ungrouped */}
          {data.recruiters.every((r) => r.role === 'recruiter') &&
            data.recruiters.length > 0 &&
            null}

          {data.recruiters.length === 0 && (
            <Card>
              <p className="px-4 py-8 text-center text-sm text-ink-muted">
                No commission allocations found for this week.
              </p>
            </Card>
          )}
        </>
      )}
    </Shell>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  KPI Strip                                                                */
/* ────────────────────────────────────────────────────────────────────────── */

function KpiStrip({ data }: { data: WeeklyCommissionData }) {
  const { companyTotal, priorWeek, companyTrend } = data;
  const gpSparkline = companyTrend.map((t) => Number(t.gp));
  const hoursSparkline = companyTrend.map((t) => Number(t.hours));
  const contractorSparkline = companyTrend.map((t) => t.contractors);
  const commRate =
    Number(companyTotal.gp) > 0
      ? (Number(companyTotal.commission) / Number(companyTotal.gp)) * 100
      : 0;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 mb-5">
      <KpiCard
        label="Gross Profit"
        value={moneyCompact(companyTotal.gp)}
        exact={money(companyTotal.gp)}
        tone="profit"
        delay={0}
        trend={priorWeek ? computeTrend(Number(companyTotal.gp), Number(priorWeek.gp)) : undefined}
        trendContext={priorWeek ? `vs ${shortDate(priorWeek.weekEnding)}` : undefined}
        sparkline={gpSparkline.length > 1 ? gpSparkline : undefined}
        sparklineColor="#16a34a"
        sparklineLabel="GP/wk"
      />
      <KpiCard
        label="Billable Hours"
        value={hours(companyTotal.hours)}
        delay={40}
        trend={
          priorWeek ? computeTrend(Number(companyTotal.hours), Number(priorWeek.hours)) : undefined
        }
        trendContext={priorWeek ? `vs ${shortDate(priorWeek.weekEnding)}` : undefined}
        sparkline={hoursSparkline.length > 1 ? hoursSparkline : undefined}
        sparklineColor="#2563eb"
        sparklineLabel="hrs/wk"
      />
      <KpiCard
        label="Active Contractors"
        value={String(companyTotal.contractors)}
        subtitle={`${data.recruiters.length} recruiter${data.recruiters.length !== 1 ? 's' : ''}`}
        delay={80}
        trend={
          priorWeek ? computeTrend(companyTotal.contractors, priorWeek.contractors) : undefined
        }
        trendContext={priorWeek ? 'vs prior week' : undefined}
        sparkline={contractorSparkline.length > 1 ? contractorSparkline : undefined}
        sparklineColor="#16a34a"
        sparklineLabel="contractors"
      />
      <KpiCard
        label="Commission Cost"
        value={moneyCompact(companyTotal.commission)}
        exact={money(companyTotal.commission)}
        subtitle={`${commRate.toFixed(1)}% of GP`}
        tone={commRate > 15 ? 'caution' : 'neutral'}
        delay={120}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  GP Distribution — Large donut + vertical legend with proportion bars     */
/* ────────────────────────────────────────────────────────────────────────── */

const DONUT_COLORS = [
  '#16a34a',
  '#2563eb',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
];

function GpDistributionDonut({
  recruiters,
  companyGp,
}: {
  recruiters: readonly WeeklyRecruiter[];
  companyGp: string;
}) {
  const sorted = [...recruiters].sort((a, b) => Number(b.grossProfit) - Number(a.grossProfit));
  const totalGp = Number(companyGp);
  if (totalGp === 0) return null;

  const maxGp = Number(sorted[0]?.grossProfit ?? 0);

  // SVG donut math
  const size = 180;
  const strokeWidth = 28;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  let arcOffset = 0;

  return (
    <Card className="mb-5">
      <CardHeader
        title="GP distribution by recruiter"
        subtitle="Share of this week's gross profit"
      />
      <div className="flex items-start gap-8 px-5 py-5">
        {/* ── Donut with center total ── */}
        <div className="flex-shrink-0 relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {/* Background ring */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={strokeWidth}
            />
            {/* Segments */}
            {sorted.map((r, i) => {
              const gp = Number(r.grossProfit);
              const pct = totalGp > 0 ? gp / totalGp : 0;
              const dash = pct * circumference;
              const gap = circumference - dash;
              const currentOffset = arcOffset;
              arcOffset += dash;
              return (
                <circle
                  key={r.id}
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${dash} ${gap}`}
                  strokeDashoffset={-currentOffset}
                  strokeLinecap="butt"
                  transform={`rotate(-90 ${center} ${center})`}
                  className="transition-all duration-500"
                />
              );
            })}
          </svg>
          {/* Center label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="figure text-lg font-bold text-ink">{moneyCompact(companyGp)}</span>
            <span className="text-[10px] text-ink-faint">total GP</span>
          </div>
        </div>

        {/* ── Vertical legend with proportion bars ── */}
        <div className="flex-1 flex flex-col justify-center gap-4 min-w-0">
          {sorted.map((r, i) => {
            const gp = Number(r.grossProfit);
            const pct = totalGp > 0 ? (gp / totalGp) * 100 : 0;
            const barPct = maxGp > 0 ? (gp / maxGp) * 100 : 0;
            const delta = r.gpDelta !== null ? Number(r.gpDelta) : null;
            const color = DONUT_COLORS[i % DONUT_COLORS.length]!;

            return (
              <div key={r.id}>
                {/* Row 1: name */}
                <div className="flex items-baseline gap-2 mb-0.5">
                  <div
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-sm font-semibold text-ink">{r.name}</span>
                </div>
                {/* Row 2: stats */}
                <div className="flex items-center gap-2 ml-[18px] mb-1.5 text-xs">
                  <span className="figure font-semibold text-ink">{money(r.grossProfit)}</span>
                  <span className="text-ink-faint">·</span>
                  <span className="text-ink-faint">{pct.toFixed(1)}%</span>
                  {delta !== null && (
                    <>
                      <span className="text-ink-faint">·</span>
                      <span
                        className={`font-semibold ${delta >= 0 ? 'text-profit' : 'text-red-500'}`}
                      >
                        {delta >= 0 ? '↑' : '↓'}
                        {Math.abs(delta).toFixed(0)}%
                      </span>
                    </>
                  )}
                </div>
                {/* Row 3: proportion bar */}
                <div className="ml-[18px] h-2 rounded-full bg-surface-overlay overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(barPct, 2)}%`, backgroundColor: color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Recruiter Section — groups cards by role                                 */
/* ────────────────────────────────────────────────────────────────────────── */

function RecruiterSection({
  title,
  recruiters,
  allRecruiters,
  expanded,
  onToggle,
  tiers,
}: {
  title: string;
  recruiters: readonly WeeklyRecruiter[];
  allRecruiters: readonly WeeklyRecruiter[];
  expanded: string | null;
  onToggle: (id: string) => void;
  tiers: readonly { threshold: string; rate: string }[];
}) {
  if (recruiters.length === 0) return null;

  const sorted = [...recruiters].sort((a, b) => Number(b.grossProfit) - Number(a.grossProfit));
  const allSorted = [...allRecruiters].sort(
    (a, b) => Number(b.grossProfit) - Number(a.grossProfit),
  );

  return (
    <div className="mb-4">
      <h2 className="text-xs font-bold uppercase tracking-wider text-ink-faint mb-2 px-1">
        {title}
      </h2>
      {sorted.map((rec) => {
        const globalRank = allSorted.findIndex((r) => r.id === rec.id) + 1;
        return (
          <RecruiterCard
            key={rec.id}
            rec={rec}
            rank={globalRank}
            isTopPerformer={globalRank === 1 && allRecruiters.length > 1}
            expanded={expanded === rec.id}
            onToggle={() => onToggle(rec.id)}
            tiers={tiers}
          />
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Recruiter Card                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

function RecruiterCard({
  rec,
  rank,
  isTopPerformer,
  expanded,
  onToggle,
  tiers,
}: {
  rec: WeeklyRecruiter;
  rank: number;
  isTopPerformer: boolean;
  expanded: boolean;
  onToggle: () => void;
  tiers: readonly { threshold: string; rate: string }[];
}) {
  const delta = rec.gpDelta !== null ? Number(rec.gpDelta) : null;
  const commRatio = Number(rec.commissionGpRatio);

  return (
    <Card className="mb-3 overflow-hidden">
      {/* ── Card header ── */}
      <button
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-surface-hover/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span
            className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              isTopPerformer
                ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300'
                : 'bg-surface-overlay text-ink-muted'
            }`}
          >
            {isTopPerformer ? '★' : rank}
          </span>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-ink">{rec.name}</span>
              {isTopPerformer && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 px-1.5 py-0.5 rounded-full bg-amber-50">
                  Top GP
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-ink-faint">
              <span>
                {rec.contractorCount} contractor{rec.contractorCount !== 1 ? 's' : ''}
              </span>
              <span>{hours(rec.hours)}h billable</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Sparkline */}
          {rec.sparkline.length > 1 && (
            <MiniSparkline values={rec.sparkline} width={60} height={20} color="#16a34a" />
          )}

          {/* Week-over-week delta */}
          {delta !== null && (
            <div className={`text-right ${delta >= 0 ? 'text-profit' : 'text-red-500'}`}>
              <span className="figure text-xs font-bold">
                {delta >= 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(0)}%
              </span>
              <p className="text-[10px] text-ink-faint">vs prior wk</p>
            </div>
          )}

          {/* GP */}
          <div className="text-right">
            <span className="figure text-sm font-bold text-profit">{money(rec.grossProfit)}</span>
            <p className="text-[10px] text-ink-faint">GP</p>
          </div>

          {/* Commission */}
          <div className="text-right">
            <span className="figure text-sm font-semibold text-revenue">
              {money(rec.commission)}
            </span>
            <p className="text-[10px] text-ink-faint">commission</p>
          </div>

          {/* Cost ratio */}
          <div className="text-right w-12">
            <span
              className={`figure text-xs font-bold ${
                commRatio > 15 ? 'text-caution' : commRatio > 10 ? 'text-ink' : 'text-profit'
              }`}
            >
              {rec.commissionGpRatio}%
            </span>
            <p className="text-[10px] text-ink-faint">cost</p>
          </div>

          {/* Tier badge */}
          {rec.tierPosition && <TierBadge tier={rec.tierPosition} />}

          {/* Expand chevron */}
          <svg
            className={`w-4 h-4 text-ink-faint transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* ── Expanded detail table — ALL columns from the spreadsheet ── */}
      {expanded && (
        <div className="border-t border-edge overflow-x-auto">
          <table className="w-full text-xs whitespace-nowrap">
            <thead>
              <tr className="bg-surface-overlay/40">
                <th className="px-3 py-2 text-left font-medium text-ink-muted">Contractor</th>
                <th className="px-3 py-2 text-left font-medium text-ink-muted">Client</th>
                <th className="px-3 py-2 text-right font-medium text-ink-muted">Hours</th>
                <th className="px-3 py-2 text-right font-medium text-ink-muted">Pay Rate</th>
                <th className="px-3 py-2 text-right font-medium text-ink-muted">Bill Rate</th>
                <th className="px-3 py-2 text-right font-medium text-ink-muted">Load</th>
                <th className="px-3 py-2 text-right font-medium text-ink-muted">Sub‑Vendor</th>
                <th className="px-3 py-2 text-right font-medium text-ink-muted">GP/Hr</th>
                <th className="px-3 py-2 text-right font-medium text-ink-muted">Gross Profit</th>
                <th className="px-3 py-2 text-right font-medium text-ink-muted">Share</th>
                <th className="px-3 py-2 text-right font-medium text-ink-muted">Allocated GP</th>
              </tr>
            </thead>
            <tbody>
              {rec.contractors.map((c, i) => {
                const gpPerHour = c.gpPerHour ? Number(c.gpPerHour) : null;

                return (
                  <tr
                    key={i}
                    className="border-b border-edge/20 hover:bg-surface-hover/30 transition-colors"
                  >
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2 text-ink-muted">{c.clientName}</td>
                    <td className="px-3 py-2 text-right font-mono">{hours(c.hours)}</td>
                    <td className="px-3 py-2 text-right font-mono text-ink-muted">
                      {c.payRate ? rate(c.payRate) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-ink-muted">
                      {c.billRate ? rate(c.billRate) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-ink-muted">
                      {c.loadFactor ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-ink-muted">
                      {c.subVendorRate ? rate(c.subVendorRate) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {gpPerHour !== null ? (
                        <span className={gpMarginColor(gpPerHour)}>{money(c.gpPerHour!)}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{money(c.grossProfit)}</td>
                    <td className="px-3 py-2 text-right font-mono text-ink-muted">
                      {(Number(c.share) * 100).toFixed(0)}%
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-profit">
                      {money(c.allocatedGp)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-surface-overlay/20 font-semibold">
                <td className="px-3 py-2" colSpan={2}>
                  Subtotal
                </td>
                <td className="px-3 py-2 text-right font-mono">{hours(rec.hours)}</td>
                <td className="px-3 py-2" colSpan={4} />
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right font-mono">{money(rec.grossProfit)}</td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right font-mono text-profit">
                  {money(rec.grossProfit)}
                </td>
              </tr>
            </tfoot>
          </table>

          {/* YTD context footer */}
          <div className="px-4 py-2.5 bg-surface-overlay/20 flex items-center justify-between text-xs border-t border-edge/30">
            <span className="text-ink-faint">
              YTD gross profit:{' '}
              <span className="figure font-semibold text-ink">{money(rec.ytdGp)}</span>
            </span>
            <span className="text-ink-faint">
              YTD commission:{' '}
              <span className="figure font-semibold text-ink">
                {money(computeCommissionFromTiers(tiers, Number(rec.ytdGp)))}
              </span>
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Tier Badge                                                               */
/* ────────────────────────────────────────────────────────────────────────── */

function TierBadge({ tier }: { tier: NonNullable<WeeklyRecruiter['tierPosition']> }) {
  return (
    <div className="flex flex-col items-center gap-0.5 w-16">
      <span className="text-[10px] font-bold text-ink-muted">
        Tier {tier.tierIndex}/{tier.totalTiers}
      </span>
      <div className="w-full h-1.5 rounded-full bg-surface-overlay overflow-hidden">
        <div
          className="h-full rounded-full bg-profit transition-all duration-500"
          style={{ width: `${tier.pct}%` }}
        />
      </div>
      <span className="text-[9px] text-ink-faint">
        {(Number(tier.rate) * 100).toFixed(0)}%
        {tier.nextTierGap && ` · ${moneyCompact(tier.nextTierGap)} to next`}
      </span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Helpers                                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

function gpMarginColor(gpPerHour: number): string {
  if (gpPerHour >= 20) return 'text-profit font-semibold';
  if (gpPerHour >= 10) return 'text-ink';
  if (gpPerHour >= 0) return 'text-caution';
  return 'text-red-500 font-semibold';
}

function computeCommissionFromTiers(
  tiers: readonly { threshold: string; rate: string }[],
  gp: number,
): string {
  if (tiers.length === 0) return '0';
  let commission = 0;
  let remaining = gp;
  for (let i = 0; i < tiers.length; i++) {
    const threshold = Number(tiers[i]!.threshold);
    const r = Number(tiers[i]!.rate);
    const nextThreshold = i + 1 < tiers.length ? Number(tiers[i + 1]!.threshold) : Infinity;
    const bandSize = nextThreshold - threshold;
    const inBand = Math.min(Math.max(remaining, 0), bandSize);
    commission += inBand * r;
    remaining -= bandSize;
  }
  return commission.toFixed(2);
}
